import React from "react";
import axios, { AxiosResponse } from "axios";
import L, { LatLngBoundsExpression } from "leaflet";

import { apiKey, apiPrefix, baseSpeedUnit, zoomRange } from "../config";

const useAgencyData = (): AgencyData | undefined | { apiError: boolean } => {
  const [loadingInitialData, setLoadingInitialData] = React.useState<boolean>(true);
  const [url, setUrl] = React.useState<string>("");
  const [agencyId, setAgencyId] = React.useState<string>("");
  const [agencyName, setAgencyName] = React.useState<string>("");
  const [initialMapRange, setInitialMapRange] = React.useState<LatLngBoundsExpression>({} as LatLngBoundsExpression);
  const [initialMapCenter, setInitialMapCenter] = React.useState<LanLngPoint>({} as LanLngPoint);
  const [lang, setLang] = React.useState<string>("");
  const [timeZone, setTimeZone] = React.useState<string>("");
  const [timeZoneOffsetMinutes, setTimeZoneOffsetMinutes] = React.useState<number | null>(null);
  const [speedUnit, setSpeedUnit] = React.useState<string>("");
  const [apiError, setApiError] = React.useState<boolean>(false);
  // console.log("apiError:", apiError);

  const getInitialData: Promise<AgencyData | undefined> = React.useMemo(() => {
    const url: string = `${apiPrefix}/v1/key/${apiKey}/command/agencies`;

    return (async function (): Promise<AgencyData | undefined> {
      try {
        const res: AxiosResponse<any, any> = await axios.get(url);
        // console.log("res:", res);

        if (res.status === 200) {
          //* Different key names in apis
          const resData: InitialFetchAgency = res?.data?.[Object.keys(res?.data)?.[0]]?.[0]; //* V2b - one line
          // console.log("resData:", resData);
          const speedUnit: string = resData?.speedUnit || baseSpeedUnit;
          // console.log({ speedUnit });

          const {
            agencyId,
            name: agencyName,
            extent,
            url,
            lang,
            timezone: timeZone,
            timezoneOffsetMinutes: timeZoneOffsetMinutes,
          } = resData || {};

          const corner1 = {
            lat: (extent?.minLat - zoomRange) as number,
            lng: (extent?.minLon - zoomRange) as number,
          } as LanLngPoint;
          const corner2 = {
            lat: (extent?.maxLat + zoomRange) as number,
            lng: (extent?.maxLon + zoomRange) as number,
          } as LanLngPoint;

          const bounds = L.latLngBounds([corner1, corner2]) as LatLngBoundsExpression;

          const initialMapCenter = {
            lat: (extent?.minLat + extent?.maxLat) / 2,
            lng: (extent?.minLon + extent?.maxLon) / 2,
          } as LanLngPoint;

          return {
            agencyId,
            agencyName,
            bounds,
            initialMapCenter,
            url,
            lang,
            timeZone,
            timeZoneOffsetMinutes,
            speedUnit,
          } as AgencyData;
        }
      } catch (error) {
        setApiError(true);
        if ((error as CustomError).response) {
          console.error("error.response.status:", ((error as CustomError)?.response as { status: number })?.status);
        }
        // throw new Error(JSON.stringify(error as CustomError));
      }
    })();
  }, []);

  //* Get base agency
  React.useEffect(() => {
    (async function (): Promise<void> {
      setLoadingInitialData(true);
      // console.trace();
      try {
        const { agencyId, agencyName, bounds, initialMapCenter, url, lang, timeZone, timeZoneOffsetMinutes, speedUnit } =
          (await getInitialData) as AgencyData;
        setAgencyId(agencyId);
        setUrl(url as string);
        setAgencyName(agencyName);
        setInitialMapRange(bounds as LatLngBoundsExpression);
        setInitialMapCenter(initialMapCenter);
        setLang(lang);
        setTimeZone(timeZone);
        setTimeZoneOffsetMinutes(timeZoneOffsetMinutes);
        setSpeedUnit(speedUnit);
      } catch (error) {
        setApiError(true);
        setLoadingInitialData(false);
        if ((error as CustomError).response) {
          console.error("error.response.status:", ((error as CustomError)?.response as { status: number })?.status);
        }
        // throw new Error(JSON.stringify(error as CustomError));
      } finally {
        setLoadingInitialData(false);
      }
    })();
  }, [getInitialData]);

  if (!loadingInitialData && apiError) {
    return {
      apiError: apiError,
    };
  }

  if (
    !loadingInitialData &&
    !apiError &&
    agencyId &&
    agencyName &&
    initialMapRange &&
    Object.keys(initialMapCenter)?.length &&
    lang &&
    timeZone &&
    timeZoneOffsetMinutes &&
    speedUnit
  ) {
    return {
      url,
      agencyId,
      agencyName,
      initialMapRange,
      initialMapCenter,
      lang,
      timeZone,
      timeZoneOffsetMinutes,
      speedUnit,
      apiError,
    } as const;
  }
};

export default useAgencyData;
